import React, {Fragment} from "react";
import {Checkbox, Col, Divider, Form, Input, Row, Select, Switch, Table} from "antd";
import {createRequest, fetchItemRequest, updateRequest} from "@actions/video-file";
import {FormattedMessage} from "react-intl";
import {ApplicationState} from "@reducers/index";
import {VideoFile} from "@models/vide-file";
import {
  VideoFileFlag,
  VideoFileFlagOptions,
  VideoFileProfileNameStringOptions,
  VideoFileTypeOptions,
  VideoFileWatermarkPosition,
  VideoFileWatermarkPositionOptions
} from "@models/video-file";
import {REGEX_PATTERNS} from "@constants/regex";
import TitrationManager from "./Titrations";
import transcoding from '@api/transcoding'
import {VideoFileProfileNameString, VideoFileStatus, VideoFileStatusOptions} from "../../models/video-file";
import debounce from "lodash/debounce";
import withFormWrapper from '@hocs/withFormWrapper';
import {validVideoFileExtensions} from "./core/validFileExtensions";
import FileAudioManager from './FileAudioManager';
import SubTitleManager from "./SubTitleManager";


interface PropsFromState {
  loading: boolean;
  errors?: string;
  form: any;
  video?: VideoFile,
}

interface PropsFromDispatch {
  createRequest: typeof createRequest;
  updateRequest: typeof updateRequest;
  fetchItemRequest: typeof fetchItemRequest;
}

type VideoFileFormAllProps = PropsFromState & PropsFromDispatch


type VideoFileFormStateShape = VideoFile & {
  visible: boolean;
  id: string | null;
};

class VideoFileForm extends React.Component<VideoFileFormAllProps, VideoFileFormStateShape> {

  titrationManagerRef = React.createRef();
  audioFileRef = React.createRef();
  subtitlesRef = React.createRef();

  constructor(props) {
    super(props);
    const {videoFile} = props;
    this.state = {
      visible: false,
      fileInfo: null,
      ...this.getStateFormProps(videoFile)
    };

    this.validateFtpPath = debounce(this.validateFtpPath, 800)
  }

  getPatchPayload = (returnValues) => {
    const patchValues = [];
    for (const key in returnValues) {
      patchValues.push({
        from: "",
        op: "replace",
        path: key,
        value: returnValues[key],
      })
    }

    if (this.showStatusInput() && this.state.status) {
      patchValues.push({
        from: "",
        op: "replace",
        path: "status",
        value: this.state.status
      })
    }

    return patchValues;
  };

  getStateFormProps = (videoFile: VideoFile | null) => {
    const obj = videoFile || {};
    const defaultProfiles = [
      VideoFileProfileNameString._360,
      VideoFileProfileNameString._480,
      VideoFileProfileNameString._560,
      VideoFileProfileNameString._720
    ];
    return {
      title: obj.title || "",
      target_file_name: obj.target_file_name || "",
      relative_ftp_path: obj.relative_ftp_path || "",
      watermark_position: obj.watermark_position || VideoFileWatermarkPosition.NONE,
      mp4_profiles: obj.mp4_profiles || defaultProfiles,
      hls_profiles: obj.hls_profiles || defaultProfiles,
      flags: (obj.flags && obj.flags != VideoFileFlag.NONE) ? obj.flags.split(",").map(flag => flag.trim()).join(",") : VideoFileFlag.NONE,
      status: obj.status || VideoFileStatus.NONE,
      titrations: obj.titrations || [],
      id: obj.id,
    };
  };

  getValues = () => {
    let returnValues = null;
    const titrations = this.titrationManagerRef.current.getValue();
    const audios = this.audioFileRef.current?.getAudios() ?? [];
    const subtitles = this.subtitlesRef.current?.getSubtitles() ?? [];

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err && titrations) {
        returnValues = {
          video:{
            ...values,
            flags: this.state.flags || VideoFileFlag.NONE,
            titrations,
            audios:audios.filter(item => !item.path).map(({...id, ...rest}) => rest)
          },
          audios:audios.filter(item => !!item.path).map(({...id, ...rest}) => rest),
          subtitles:subtitles.filter(item => !!item.path).map(({...id, ...rest}) => rest)
        };
      }
    });

    const patch = this.state.id ? true : false;

    return {
      payload: !patch ? returnValues : this.getPatchPayload(returnValues),
      id: this.props.videoFile ? this.props.videoFile.id : null,
      patch
    };
  };

  onChangeFlags = (checkedValues) => {
    this.setState({
      flags: checkedValues.join(",")
    });
  };

  validateFtpPath = (rule, value, callback) => {
    const path = value.trim();
    this.setState(preveState => ({
      ...preveState,
      fileInfo: null
    }))
    if (!!path) {
      const extention = path.split(".").pop()
      if (!validVideoFileExtensions.includes(extention)) {
        callback(
          <FormattedMessage
            id="invalid_video_file_extension"
            defaultMessage={`File extension is invalid!`}/>);
      } else {
        transcoding.getFile({params: {path}})
          .then(({data}) => {
            this.setState(preveState => ({
              ...preveState,
              fileInfo: data
            }))
            if (!data) {
              callback(
                <FormattedMessage
                  id="app.invalid_file_path"
                  defaultMessage={`File path is invalid`}/>);
            } else {
              callback();
            }
          })
          .catch(error => callback(<FormattedMessage
            id="app.invalid_file_path"
            defaultMessage={`File path is invalid`}/>));
      }

    } else {
      callback();
    }
  };

  onStatusChange = (active) => {
    this.setState({
      status: active ? VideoFileStatus.ACTIVE : VideoFileStatus.DISABLE
    })
  };

  showStatusInput = () => {
    const {videoFile = {}} = this.props;
    return this.state.id &&
      (videoFile.status == VideoFileStatus.ACTIVE || videoFile.status == VideoFileStatus.DISABLE);
  };


  getFileInfo = () => {
    const {audios, size, ...rest} = this.state.fileInfo;
    const columns = [
      ...Object.keys(rest).map(key => ({
        key,
        render: (record) => record[key] === true ? "True" : record[key],
        title: <FormattedMessage id={key} defaultMessage={key}/>})),
      {
        key: "width",
        render: (record) => size?.width,
        title: <FormattedMessage id={"width"} defaultMessage={"width"}/>
      },
      {
        key: "height",
        render: (record) => size?.height,
        title: <FormattedMessage id={"height"} defaultMessage={"height"}/>
      }
    ]
    return(
      <Table
        bordered
        dataSource={[rest]}
        columns={columns}
        pagination={false}
      />
    )
  }

  render() {
    const {videoFile = {}, form} = this.props;
    const {getFieldDecorator} = form;
    const showChangeStatusSwitch = true;// this.showStatusInput()

    return (
      <Form>
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item
              label={
                <FormattedMessage
                  id="app.status"
                  defaultMessage={`Status`}
                />
              }
            >
              {getFieldDecorator("status", {
                initialValue: this.state.status
              })(
                <Select disabled>
                  {VideoFileStatusOptions.map((opt, index) => (
                    <Option key={index} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label={<FormattedMessage id="app.type" defaultMessage={`Type`}/>}>
              {getFieldDecorator("type", {
                initialValue: videoFile.type
              })(
                <Select>
                  {VideoFileTypeOptions.filter(opt => opt.value != "none").map((opt, index) => (
                    <Option key={index} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label={
                <FormattedMessage id="app.title" defaultMessage={`Title`}/>
              }
            >
              {getFieldDecorator("title", {
                initialValue: this.state.title,
                rules: [
                  {
                    whitespace: true,
                    required: this.state.id ? false : true,
                    message: (
                      <FormattedMessage
                        id="app.title_is_required"
                        defaultMessage={`Title is required`}
                      />
                    )
                  }
                ]
              })(<Input/>)}
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label={<FormattedMessage id="app.english_name" defaultMessage={`English Name`}/>}>
              {getFieldDecorator("english_name", {
                initialValue: this.state.english_name,
                rules: [
                  {
                    whitespace: true,
                    required: this.state.id ? false : true,
                    message: (
                      <FormattedMessage
                        id="app.english_name_is_required"
                        defaultMessage={`Title is required`}
                      />
                    )
                  }
                ]
              })(<Input/>)}
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label={
                <FormattedMessage id="app.target_file_name" defaultMessage={`Target File Name`}/>
              }
            >
              {getFieldDecorator("target_file_name", {
                initialValue: this.state.target_file_name,
                rules: [
                  {
                    whitespace: true,
                    required: this.state.id ? false : true,
                    message: (
                      <FormattedMessage
                        id="app.target_file_name_is_required"
                        defaultMessage={`Target file name is required!`}
                      />
                    )
                  }
                ]
              })(<Input/>)}
            </Form.Item>
          </Col>

          <Col span={6} className={'flex-display'}>
            <div className={'custom-input-control-wrapper'}>
              <Checkbox.Group
                options={VideoFileFlagOptions}
                value={this.state.flags.split(",")}
                onChange={this.onChangeFlags}
              />
            </div>
          </Col>

          <Col span={6} className={'flex-display'}>
            {showChangeStatusSwitch && (
              <div className={'custom-input-control-wrapper'}>
                <label><FormattedMessage id="app.active" defaultMessage={`Active`}/></label>
                <Switch
                  defaultChecked={videoFile.status === VideoFileStatus.ACTIVE}
                  onChange={this.onStatusChange}
                />
              </div>
            )}
          </Col>

          <Col span={24}>
            <Form.Item label={<FormattedMessage id="app.relative_ftp_path"
                                                defaultMessage={`Relative FTP path `}/>}>
              {getFieldDecorator("path", {
                initialValue: this.state.relative_ftp_path,
                // validateTrigger: "onBlur",
                rules: [{
                  whitespace: true,
                  required: this.state.id ? false : true,
                  message: (
                    <FormattedMessage
                      id="app.ftp_path_is_required"
                      defaultMessage={`FTP path is required`}
                    />
                  )
                }, {
                  validator: this.validateFtpPath
                }]
              })(
                <Input className={"url-input"}/>
              )}
            </Form.Item>
          </Col>
        </Row>

        {this.state.fileInfo && (
          <div>
            <Divider dashed={true} orientation="left">
              <FormattedMessage id="file_info" defaultMessage={`File Info`}/>
            </Divider>
            {this.getFileInfo()}

            <Divider dashed={true} orientation="left">
              <FormattedMessage id="audios" defaultMessage={`Audios`}/>
            </Divider>
            <FileAudioManager ref={this.audioFileRef} audios={this.state.fileInfo.audios ?? []}/>

            <Divider dashed={true} orientation="left">
              <FormattedMessage id="subtitles" defaultMessage={`Subtitles`}/>
            </Divider>

            <SubTitleManager ref={this.subtitlesRef} subtitles={this.state.fileInfo.subtitles ?? []}/>
          </div>
        )}

        <Row>
          <Divider dashed={true} orientation="left">
            <FormattedMessage id="app.titrations" defaultMessage={`Titrations`}/>
          </Divider>
          <TitrationManager ref={this.titrationManagerRef}
                            titrations={this.state.titrations}/>
        </Row>

      </Form>
    );
  }
}


export default withFormWrapper(Form.create({name: "video-file-form"})(VideoFileForm));

